import * as R from 'remeda'

import {
  compact,
  exists,
  findByPath,
  mapNineSegment,
  type NineSegmentCounts,
  pathOr,
  toNineNumber,
} from '~/utils'

import { type NineSegmentsData } from './nineSegments'

export type NineSegmentsCustomerAttributeData = NineSegmentsData & {
  customerAttribute: { id: string; name: string }
  customerAttributeGroup: { name: string }
  number: number
}

// Minimal interfaces using structural typing so both the StandardAnalysisPage hook and
// basic output can satisfy them. Arrays are mutable because findByPath takes `T[]`.
type CustomerAttributeResult = {
  byCustomerAttributeId: string
  result: {
    brands: {
      brandNumber: number
      result: { count: number; segmentNumber: number }[]
    }[]
    totalCount: number
  }
}

export type NineSegmentsCustomerAttributesArrayDataInput = {
  // The table has column slots for exactly ONE brand, so the caller picks which one.
  brandNumber: number
  customerAttributeGroups: ReadonlyArray<{
    customerAttributes: ReadonlyArray<{ id: string; name: string }>
    name: string
  }>
  customerAttributeResults: CustomerAttributeResult[]
  // Curried per-row builder from useGetNineSegmentsData.
  getNineSegmentsData: (
    nineSegmentCounts: NineSegmentCounts,
    total: number,
  ) => NineSegmentsData
  // Sort function from useAtomValue(nineSegmentSortFunctionAtom); basic-output supplies
  // identity for offline render.
  sort: (
    rows: NineSegmentsCustomerAttributeData[],
  ) => NineSegmentsCustomerAttributeData[]
}

export const buildNineSegmentsCustomerAttributesArrayData = (
  input: NineSegmentsCustomerAttributesArrayDataInput,
): NineSegmentsCustomerAttributeData[][] =>
  R.pipe(
    input.customerAttributeGroups,
    R.map((customerAttributeGroup) =>
      R.pipe(
        customerAttributeGroup.customerAttributes,
        R.map((customerAttribute) => {
          const result = findByPath(
            input.customerAttributeResults,
            'byCustomerAttributeId',
            customerAttribute.id,
          )?.result
          // No data for this attribute ⇒ no row (rather than a row of NaN), as the live page does
          if (!exists(result)) {
            return null
          }

          const segmentCounts = R.pipe(
            result.brands,
            findByPath('brandNumber', input.brandNumber),
            pathOr('result', []),
          )
          const nineSegmentCounts = mapNineSegment((nineSegment) =>
            R.pipe(
              segmentCounts,
              findByPath('segmentNumber', toNineNumber(nineSegment)),
              pathOr('count', NaN),
            ),
          )

          return {
            customerAttribute,
            customerAttributeGroup,
            ...input.getNineSegmentsData(nineSegmentCounts, result.totalCount),
          }
        }),
        compact,
        // Numbered AFTER dropping data-less attributes so they stay contiguous within the block
        R.map((data, index) => ({ ...data, number: index + 1 })),
        input.sort,
      ),
    ),
    R.filter((customerAttributesData) => 0 < customerAttributesData.length),
  )
